/**
 * @module app.core
 */

import { TaskQueue, autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { NoteService } from 'app/core/services/note-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { localSort } from 'app/shared/lib/sorting';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NoteCategoryReloadEvent } from 'app/core/lib/events';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';

const expandedCategoriesCache = new Map<string, Array<Core.NoteDefinition>>();


@autoinject
export class StandardNotesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey = "";

    @bindable
    protected rememberExpand: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig?: INotesConfig;

    protected noteCategoriesReload: Subscription | null = null;

    protected noteCategories: Core.NoteCategory[] = [];
    protected editedStandardNote: Core.NoteDefinition = {};
    private sorting = new Sorting();
    protected pageSize = 10;
    protected resultCount = 0;
    protected includeInactive = false;

    protected validate = validate;
    protected isValid = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected captionPrefix: string | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<Core.NoteDefinition[]>;
    protected $modal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: NoteService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // verify that component is properly configured
        if(!this.notesConfig) throw new Error('Bindable property [notes-config] was not set');
        if(!this.notesConfig?.serviceRootUrl) throw new Error('[serviceRootUrl] was not configured on bindable property [notes-config]');
        if(!this.notesConfig?.moduleCode) throw new Error('[moduleCode] was not configured on bindable property [notes-config]');
        this.expandedCacheKey = `${this.notesConfig.moduleCode.toLowerCase()}-manage-standard-notes`;
        this.service.configure(this.notesConfig);

        // listen for updates from the Note Categories tab
        this.noteCategoriesReload = this.events.subscribe(NoteCategoryReloadEvent, async (e: NoteCategoryReloadEvent) => {
            await this.reload();
        });
    }

    protected async attached(): Promise<void> {
        await this.reload();
    }

    ////****************** Events ***************************************************////
    protected async handleFormSuccess(): Promise<void> {
        this.$modal.close();
        await this.reload();
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected applyLocalSort(event: SortChangeEvent, standardNotes: (Core.NoteDefinition)[]): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(standardNotes, sorting);
        if(this.sorting.entries.length == 0) {
            this.reload();
        };
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadNoteCategories();
    }

    protected saveStandardNote(standardNote: Core.NoteDefinition = {}): void {
        this.editedStandardNote = deepClone(standardNote);
        this.$modal.open();
    }

    protected restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the outcomes into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedCategories = expandedCategoriesCache.get(this.expandedCacheKey);
            if(expandedCategories !== undefined) {
                this.$table.restoreExpanded(expandedCategories);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<Core.NoteDefinition>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedCategoriesCache.set(this.expandedCacheKey, detail.items);
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadNoteCategories(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes = [
            'definitions',
            'definitions.category'
        ];

        const fields = [
            'definitions.category.id',
            'definitions.code',
            'definitions.description',
            'definitions.text',
            'description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCategories(options, includes, false, false, true, fields);
            this.noteCategories = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateStandardNote(standardNote: Core.NoteDefinition): Promise<void> {
        if(!standardNote.id) return;

        // Show confirmation dialog only if deactivating
        if(standardNote.isActive) {
            const result = await confirmation.showDeactivate('core:note.definition.$', 1);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateStandardNote(standardNote.id, !standardNote.isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:note.definition.$', !standardNote.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}


